import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { FILTER_DEFINITIONS, filtersError, type ManifestFilters, type Measures, REPORT_VIEWS, type ReportView, PERIOD_MODES, periodWindow, type PeriodMode } from './model';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | null> }>;
  truncated: boolean;
};

export function ltlpManifestPageView(input: {
  ready: boolean;
  filters: ManifestFilters;
  options: Record<string, Array<string>>;
  period: string;
  totals: Measures | null;
  section: PageSection | null;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    reportView: { type: 'enum', values: REPORT_VIEWS.map((view) => view.value) },
    periodMode: { type: 'enum', values: [...PERIOD_MODES] },
    from: { type: 'date' },
    to: { type: 'date' },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => {
        const values = options[key] ?? [];
        return [key, values.length > 100
          ? { type: 'text', multiple: true, maxSelections: 200, maxLength: 4000 }
          : { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...values] }];
      }),
    ),
  };
  const section = input.section;
  return {
    title: 'LTLP Manifest',
    ready: input.ready,
    dataMode: 'live reporting.sales',
    filters: {
      reportView: filters.reportView,
      periodMode: filters.periodMode,
      from: filters.from,
      to: filters.to,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].join(','),
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    totals: input.totals,
    visibleSections: section
      ? [
          {
            title: section.title,
            totalRows: section.rows.length,
            rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
            truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
          },
        ]
      : [],
    definitions: {
      source:
        'lux-analytics-prod.reporting.sales, LTLP only (product_bk bedbank_marriott); queried live, rebuilt daily.',
      dates:
        'Sydney transaction dates. Bookings opens on last week (Monday to Sunday); Vendor Summary on this year to yesterday.',
      measures:
        'Gross sales, margin and cost sum gross_sales_aud, margin_aud and cost_sales_aud; packages and nights sum number_of_packages and number_of_nights.',
      filters:
        'Filters accept comma-separated listed values; an empty string selects all. Values containing commas cannot be edited here; activeSelections lists the effective values. Exclude Old Margin Maxed Offers is False only for margin-maxed rows that are not the item’s latest rebooking.',
    },
  };
}

export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: ManifestFilters,
  options: Record<string, Array<string>>,
): Partial<ManifestFilters> {
  const validation = { ...view.filterSchema };
  for (const { key } of FILTER_DEFINITIONS) {
    validation[key] = { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...(options[key] ?? [])] };
  }
  validatePageFilters(changes, validation);
  const next: Partial<ManifestFilters> = {};
  if ('reportView' in changes) {
    next.reportView = changes.reportView as ReportView;
  }
  if ('periodMode' in changes) {
    const periodMode = changes.periodMode as PeriodMode;
    next.periodMode = periodMode;
    if (periodMode !== 'custom') Object.assign(next, periodWindow(periodMode));
  }
  if (('from' in changes || 'to' in changes) && 'periodMode' in changes && changes.periodMode !== 'custom') {
    throw new Error('Choose custom dates before editing the date range.');
  }
  if ('from' in changes || 'to' in changes) next.periodMode = 'custom';
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (!(key in changes)) {
      continue;
    }
    const value = changes[key] as string;
    selections[key] = value.split(',').map((item) => item.trim()).filter(Boolean);
    changed = true;
  }
  if (changed) {
    next.selections = selections;
  }
  const error = filtersError({ ...filters, ...next });
  if (error) throw new Error(error);
  return next;
}
